/**
 * @copyright
 * Copyright (c) 2024 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import Flex from "@atg/icf-react-components/components/Flex";
import IconButton from "@atg/icf-react-components/components/IconButton";
import LoadingIndicator from "@atg/icf-react-components/components/LoadingIndicator";
import Text from "@atg/icf-react-components/components/Text";
import Tooltip from "@atg/icf-react-components/components/Tooltip";
import ITheme from "@atg/icf-react-components/themes/Theme";
import useColor from "@atg/icf-react-components/utils/useColor";
import { TFunction } from "i18next";
import { split } from "lodash";
import { FC, useState } from "react";
import { createUseStyles } from "react-jss";
import { useDispatch, useSelector } from "react-redux";

import CustomLink from "~/components/CustomLink";
import { RequestStatus } from "~/constants";
import {
  ButtonNameEnum,
  DeviceTypeEnum,
  DeviceTypeMapper,
  GroupByEnum,
  GroupByMapper,
  ReportStatusEnum,
  ReportStatusTooltip,
  ReportTypeEnum,
  ReportTypeMap,
} from "~/features/reportTracker/constants";
import { reportTrackerActions } from "~/features/reportTracker/slice";
import { deleteReportAsync } from "~/features/reportTracker/thunks";
import { ReportDetails } from "~/features/reportTracker/types";
import { StatisticsGroupBy, StatisticsPlatform, StatisticsTab } from "~/features/statistics/constants";
import { selectLocalReports } from "~/features/statistics/selectors";
import { StatisticsActions } from "~/features/statistics/slice";
import { AppDispatch } from "~/state/types";
import { ReportType } from "~/types";
import formatDate, { DateFormat } from "~/utils/formatDate";
import reportError from "~/utils/reportError";

const useStyle = createUseStyles((theme: ITheme) => ({
  mainButton: {
    border: "none",
    height: "40px",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    color: theme.colors.baseContrast085,
    "&:hover": {
      color: theme.colors.baseContrast085,
    },
  },
}));
interface Props {
  data: ReportDetails;
  t: TFunction;
  sitesByName: { [p: string]: number };
}

const ReportRow: FC<Props> = ({ data, t, sitesByName }) => {
  const classes = useStyle();
  const baseColor = useColor("pageBackground");
  const hoverColor = useColor("baseContrast010");
  const [bgColor, setBgColor] = useState(baseColor);
  const [isMouseOver, setIsMouseOver] = useState(false);
  const [isBeingDeleted, setIsBeingDeleted] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const localReports = useSelector(selectLocalReports);

  const mappedTab = ReportTypeMap[data.params.reportType as ReportTypeEnum];
  const mappedGroupBy = GroupByMapper[data.params.groupBy as GroupByEnum];

  const setStatisticsPageState = () => {
    dispatch(StatisticsActions.clearState);
    dispatch(StatisticsActions.setOpenedFromReportTracker(true));
    dispatch(StatisticsActions.setActiveTab(StatisticsTab[mappedTab]));
    dispatch(reportTrackerActions.setReportCacheKey(data.cacheKey));
    const mappedDevice = DeviceTypeMapper[data.params.device as DeviceTypeEnum];

    const selectedSiteIds = Object.entries(sitesByName)
      .filter(([key]) => !data.params.excludedSites.includes(key))
      .map((key) => Number(sitesByName[key[0]]));

    dispatch(
      StatisticsActions.setFilter({
        from: split(data.params.from, " ")[0],
        to: split(data.params.to, " ")[0],
        selectedSiteIds,
        trkid: data.params.trackingId ?? "",
        dev: StatisticsPlatform[mappedDevice] ?? "",
        groupBy: StatisticsGroupBy[mappedGroupBy],
      }),
    );

    dispatch(StatisticsActions.setScrollToTable(true));
    setTimeout(() => {
      dispatch(StatisticsActions.setScrollToTable(false));
    }, 1000);
  };

  const deleteReport = () => {
    const convertedReportType = data.params.reportType as ReportType;
    dispatch(reportTrackerActions.setReportCacheKey(undefined));
    if (data.status === ReportStatusEnum.PENDING || localReports[convertedReportType].cacheKey === data.cacheKey) {
      dispatch(
        StatisticsActions.setReport({
          type: convertedReportType,
          status: RequestStatus.IDLE,
          cacheKey: undefined,
          data: [],
        }),
      );
    }

    const thunk = deleteReportAsync({ cacheKey: data.cacheKey });
    dispatch(thunk)
      .unwrap()
      .catch((error) => {
        reportError(error);
      });
  };

  const isSuccess = data.status === ReportStatusEnum.SUCCESS;
  const isFailed = data.status === ReportStatusEnum.FAILED;
  const isPending = data.status === ReportStatusEnum.PENDING;
  const isMarkedForDeletion = data.status === ReportStatusEnum.MARKED_FOR_DELETION;

  const reportDate = data.params.lastPayouts
    ? `- ${t("intervals.last_x_payouts", { x: data.params.lastPayouts, ns: "common" })}`
    : `${formatDate(data.params.from, DateFormat.MEDIUM)} - ${formatDate(data.params.to, DateFormat.MEDIUM)}`;
  const reportName = t(`report_type.${data.params.reportType}`);

  return (
    <div
      onMouseOver={() => {
        setBgColor(hoverColor);
        setIsMouseOver(true);
      }}
      onFocus={() => {
        setBgColor(hoverColor);
        setIsMouseOver(true);
      }}
      onMouseLeave={() => {
        setBgColor(baseColor);
        setIsMouseOver(false);
      }}
      style={{ backgroundColor: bgColor }}
      hidden={isBeingDeleted && !isMarkedForDeletion}
    >
      <Flex direction="row" alignItems="center" justifyContent="space-between" padding="0 $0.5 0 $2" height="48px">
        <CustomLink
          className={classes.mainButton}
          onClick={isSuccess ? setStatisticsPageState : () => null}
          navigateEnabled={isSuccess}
          style={{ backgroundColor: bgColor, cursor: isSuccess ? "pointer" : "default" }}
          to="app/statistics#table"
        >
          <Tooltip title={t(ReportStatusTooltip[data.status])}>
            <Text variant="body_m" style={{ whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}>
              {`${reportName} ${reportDate}`}
            </Text>
          </Tooltip>
        </CustomLink>

        <Flex direction="row">
          {isPending && !isMouseOver && (
            <LoadingIndicator size={16} thickness={2} color="lightgray" marginRight="11px" marginLeft="10px" />
          )}
          {isSuccess && !isMouseOver && <IconButton fill="success" name={ButtonNameEnum.SUCCESS} />}
          {isFailed && !isMouseOver && <IconButton fill="alert" name={ButtonNameEnum.FAILED} />}
          {isMarkedForDeletion && <IconButton fill="alert" name={ButtonNameEnum.MARKED_FOR_DELETION} />}
          {isMouseOver && !isMarkedForDeletion && (
            <IconButton
              name={ButtonNameEnum.DELETE}
              onClick={() => {
                setIsBeingDeleted(true);
                deleteReport();
              }}
              title={t("tool_tip_delete")}
            />
          )}
        </Flex>
      </Flex>
    </div>
  );
};

export default ReportRow;
